import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';
import { getCartFromLocal, saveCartToLocal, clearGuestCart } from '@/utils/cart';
import { selectUser } from './authSlice';

// Initialize cart from localStorage for guests (if available)
// This ensures cart persists across page reloads
const getInitialCartItems = () => {
  try {
    // Only load from localStorage if we're in the browser
    if (typeof window !== 'undefined') {
      return getCartFromLocal();
    }
    return [];
  } catch (error) {
    console.error('Error loading initial cart from localStorage:', error);
    return [];
  }
};

// Initial state
const initialState = {
  items: getInitialCartItems(), // Load from localStorage on app start
  loading: false,
  error: null,
  lastSynced: null,
  totals: null, // Cart totals from backend
  totalsLoading: false,
  coupon: null, // Applied coupon info: { id, code, discountType, discountValue, discountAmount }
  couponLoading: false,
  couponError: null,
};

// Async thunks
export const loadCart = createAsyncThunk(
  'cart/loadCart',
  async (user, { rejectWithValue }) => {
    try {
      if (user) {
        // Logged in — fetch from server
        const res = await axios.get('/auth/cart');
        const cart = res.data.cart || [];
        
        // Normalize cart items to handle new API structure
        // Backend returns: basePrice, discount, finalPrice, stock
        // Map to our internal structure while maintaining backward compatibility
        return cart.map(item => ({
          id: item.id,
          productId: item.productId,
          name: item.name,
          basePrice: item.basePrice || item.price || 0,
          discount: item.discount || 0,
          finalPrice: item.finalPrice || item.price || 0,
          price: item.finalPrice || item.price || 0, // Alias for backward compatibility
          originalPrice: item.basePrice || item.price || 0, // For display
          quantity: item.quantity,
          stock: item.stock || item.stock_quantity || 0,
          stock_quantity: item.stock || item.stock_quantity || 0, // Alias for backward compatibility
          image: item.image,
          variationId: item.variationId || null, // Include variation ID if present
          variation: item.variation || null, // Include full variation data if present
        }));
      } else {
        // Guest — load from localStorage
        // Ensure we always return an array, even if localStorage is empty
        const localCart = getCartFromLocal();
        return Array.isArray(localCart) ? localCart : [];
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      // Fallback to localStorage for guests
      if (!user) {
        const localCart = getCartFromLocal();
        return Array.isArray(localCart) ? localCart : [];
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to load cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ product, quantity = 1, user }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentCart = state.cart.items;
      const cart = [...currentCart];

      // Use the price that's already calculated by the component
      // Components handle discount calculation (either flat or percentage)
      // The product.price passed is already the final discounted price
      const finalPrice = product.price;
      
      // Try to get original price from product.originalPrice if available
      // Otherwise, if discount exists, calculate it back
      const originalPrice = product.originalPrice || 
        (product.discount > 0 ? finalPrice + product.discount : finalPrice);

      // Find existing item - consider variation ID if present
      // Same product with different variations should be separate items
      const existingItem = cart.find(
        (item) => {
          const sameProduct = item.productId === product.productId;
          const sameVariation = 
            (!item.variationId && !product.variationId) || // both have no variation
            (item.variationId && product.variationId && item.variationId === product.variationId); // same variation
          return sameProduct && sameVariation;
        }
      );

      if (existingItem) {
        existingItem.quantity += quantity;
        // Update stock_quantity if provided
        if (product.stock_quantity !== undefined) {
          existingItem.stock_quantity = product.stock_quantity;
        }
        // Update price if it changed (e.g., discount updated)
        existingItem.price = finalPrice;
        if (originalPrice !== finalPrice) {
          existingItem.originalPrice = originalPrice;
        }
        // Update variation data if provided
        if (product.variation) {
          existingItem.variation = product.variation;
        }
        if (product.variationId) {
          existingItem.variationId = product.variationId;
        }
      } else {
        cart.push({
          id: product.id,
          productId: product.productId,
          name: product.name,
          price: finalPrice, // store discounted price
          originalPrice: originalPrice, // keep original for reference
          discount: product.discount || 0, // store discount amount
          image: product.image,
          quantity,
          stock_quantity: product.stock_quantity, // store stock quantity
          variationId: product.variationId || null, // store variation ID if present
          variation: product.variation || null, // store full variation data if present
        });
      }

      // Save to localStorage (for guests)
      // This ensures cart persists across page reloads
      try {
        saveCartToLocal(cart);
        // console.log('Cart saved to localStorage:', cart.length, 'items');
      } catch (error) {
        console.error('Failed to save cart to localStorage:', error);
      }

      // Show toast
      toast.success(`${product.name} has been added to your cart!`);

      // Sync to server if logged in
      if (user) {
        try {
          await axios.post('/auth/cart/sync', {
            items: cart.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
              variationId: item.variationId || null, // include variation ID if present
            })),
          });
        } catch (err) {
          console.error('Failed to sync cart:', err);
          toast.error('Failed to sync cart with server.');
        }
      }

      return cart;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to cart');
    }
  }
);

export const updateQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ productId, change, user, variationId = null }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // Find item by productId and variationId (if provided)
      const item = state.cart.items.find(item => 
        item.productId === productId && 
        ((!item.variationId && !variationId) || (item.variationId === variationId))
      );
      
      if (!item) {
        return rejectWithValue('Item not found in cart');
      }

      // Validate stock before allowing quantity increase
      const newQuantity = item.quantity + change;
      const stock = item.stock || item.stock_quantity || 0;
      
      if (change > 0 && stock > 0 && newQuantity > stock) {
        toast.warning(`Only ${stock} items available in stock`);
        return rejectWithValue(`Only ${stock} items available`);
      }

      const updated = state.cart.items.map((item) => {
        const matchesProduct = item.productId === productId;
        const matchesVariation = 
          (!item.variationId && !variationId) || 
          (item.variationId === variationId);
        
        return (matchesProduct && matchesVariation)
          ? { ...item, quantity: Math.max(1, Math.min(newQuantity, stock || newQuantity)) }
          : item;
      });

      // Save to localStorage (for guests)
      try {
        saveCartToLocal(updated);
        // console.log('Cart updated in localStorage:', updated.length, 'items');
      } catch (error) {
        console.error('Failed to save cart to localStorage:', error);
      }

      // Sync to server if logged in
      if (user) {
        try {
          await axios.post('/auth/cart/sync', {
            items: updated.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
              variationId: item.variationId || null,
            })),
          });
          // Reload cart to get updated stock info from backend
          // This ensures we have the latest stock after sync
          // Note: The cart will be reloaded by the component's useEffect when items change
        } catch (err) {
          console.error('Failed to sync cart:', err);
          toast.error('Failed to update quantity. Please try again.');
          // On error, still return updated local state
          return updated;
        }
      }

      return updated;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update quantity');
    }
  }
);

export const removeItem = createAsyncThunk(
  'cart/removeItem',
  async ({ productId, user, variationId = null }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // Remove item by productId and variationId (if provided)
      const updated = state.cart.items.filter((item) => {
        const matchesProduct = item.productId === productId;
        const matchesVariation = 
          (!item.variationId && !variationId) || 
          (item.variationId === variationId);
        // Keep item if it doesn't match both productId and variationId
        return !(matchesProduct && matchesVariation);
      });

      // Save to localStorage (for guests)
      try {
        saveCartToLocal(updated);
        // console.log('Cart updated in localStorage:', updated.length, 'items');
      } catch (error) {
        console.error('Failed to save cart to localStorage:', error);
      }

      // Sync to server if logged in
      if (user) {
        try {
          await axios.post('/auth/cart/remove', { 
            productId,
            variationId: variationId || null,
          });
        } catch (err) {
          console.error('Failed to remove item:', err);
        }
      }

      return updated;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove item');
    }
  }
);

export const syncCartToServer = createAsyncThunk(
  'cart/syncCartToServer',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const cart = state.cart.items;

      await axios.post('/auth/cart/sync', {
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
      });

      clearGuestCart();
      toast.success('Cart synced successfully.');

      return { synced: true, timestamp: Date.now() };
    } catch (error) {
      console.error('Failed to sync cart', error);
      toast.error('Failed to sync cart. Try again.');
      return rejectWithValue(error.response?.data?.message || 'Failed to sync cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (user, { rejectWithValue }) => {
    try {
      // Always clear localStorage first
      clearGuestCart();
      
      // If user is logged in, also clear database cart
      if (user) {
        try {
          // Clear database cart by syncing with empty array
          await axios.post('/auth/cart/sync', {
            items: [],
          });
        } catch (err) {
          console.error('Failed to clear database cart:', err);
          // Don't fail the whole operation if database clear fails
          // localStorage is already cleared
        }
      }
      
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart');
    }
  }
);

// Validate coupon code
export const validateCoupon = createAsyncThunk(
  'cart/validateCoupon',
  async ({ code, orderAmount, productIds = [] }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/coupons/validate', {
        code,
        order_amount: orderAmount,
        product_ids: productIds,
      });

      if (response.data.success && response.data.data) {
        return response.data.data.coupon;
      }

      return rejectWithValue(response.data.message || 'Invalid coupon');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to validate coupon';
      return rejectWithValue(message);
    }
  }
);

// Remove coupon
export const removeCoupon = createAsyncThunk(
  'cart/removeCoupon',
  async (_, { rejectWithValue }) => {
    return null; // Simply clear the coupon
  }
);

// Fetch cart totals from backend
export const fetchCartTotals = createAsyncThunk(
  'cart/fetchCartTotals',
  async ({ shippingCost = 0, discountAmount = 0 }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const user = selectUser(state);
      
      if (!user) {
        // For guests, calculate locally (fallback)
        const subtotal = selectCartSubtotal(getState());
        // Use the provided shippingCost (which may be 0 if not calculated yet)
        const shipping = Number(shippingCost) || 0;
        const tax = subtotal * 0.08; // Fallback tax calculation
        const total = subtotal + shipping + tax - discountAmount;
        
        return {
          subtotal,
          shippingCost: shipping,
          taxAmount: tax,
          discountAmount,
          total,
        };
      }

      // Fetch from backend for logged-in users
      // Ensure shippingCost is a number (not null/undefined)
      const shippingCostNum = Number(shippingCost) || 0;
      const discountAmountNum = Number(discountAmount) || 0;
      
      const response = await axios.get('/auth/cart/totals', {
        params: {
          shippingCost: shippingCostNum,
          discountAmount: discountAmountNum,
        },
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return rejectWithValue('Invalid response from server');
    } catch (error) {
      console.error('Failed to fetch cart totals:', error);
      // Fallback to local calculation
      const state = getState();
      const subtotal = selectCartSubtotal(state);
      const shipping = subtotal > 50 ? 0 : shippingCost || 8;
      const tax = subtotal * 0.08;
      const total = subtotal + shipping + tax - discountAmount;
      
      return {
        subtotal,
        shippingCost: shipping,
        taxAmount: tax,
        discountAmount,
        total,
      };
    }
  }
);

// Cart slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Sync localStorage to state (for cartUpdated events)
    syncFromLocalStorage: (state) => {
      const localCart = getCartFromLocal();
      // Ensure we always set an array, even if localStorage is empty or corrupted
      state.items = Array.isArray(localCart) ? localCart : [];
    },
  },
  extraReducers: (builder) => {
    // Load cart
    builder
      .addCase(loadCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCart.fulfilled, (state, action) => {
        state.loading = false;
        // Always update with the payload (which comes from localStorage for guests)
        // This ensures the cart is in sync with localStorage
        if (Array.isArray(action.payload)) {
          state.items = action.payload;
        } else {
          // If payload is not an array, set to empty array
          state.items = [];
        }
        state.error = null;
      })
      .addCase(loadCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Fallback to localStorage
        state.items = getCartFromLocal();
      });

    // Add to cart
    builder
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update quantity
    builder
      .addCase(updateQuantity.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateQuantity.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(updateQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Remove item
    builder
      .addCase(removeItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(removeItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Sync to server
    builder
      .addCase(syncCartToServer.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncCartToServer.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSynced = action.payload.timestamp;
        state.error = null;
      })
      .addCase(syncCartToServer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Clear cart
    builder
      .addCase(clearCart.fulfilled, (state) => {
        state.items = [];
        state.error = null;
        state.totals = null;
        state.coupon = null; // Clear coupon when cart is cleared
        state.couponError = null;
      });

    // Fetch cart totals
    builder
      .addCase(fetchCartTotals.pending, (state) => {
        state.totalsLoading = true;
      })
      .addCase(fetchCartTotals.fulfilled, (state, action) => {
        state.totalsLoading = false;
        state.totals = action.payload;
      })
      .addCase(fetchCartTotals.rejected, (state, action) => {
        state.totalsLoading = false;
        // Keep previous totals on error
      });

    // Validate coupon
    builder
      .addCase(validateCoupon.pending, (state) => {
        state.couponLoading = true;
        state.couponError = null;
      })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.coupon = action.payload;
        state.couponError = null;
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.coupon = null;
        state.couponError = action.payload;
      });

    // Remove coupon
    builder
      .addCase(removeCoupon.fulfilled, (state) => {
        state.coupon = null;
        state.couponError = null;
      });
  },
});

export const { syncFromLocalStorage } = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartLoading = (state) => state.cart.loading;
export const selectCartError = (state) => state.cart.error;
export const selectCartCount = (state) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
export const selectCartSubtotal = (state) =>
  state.cart.items.reduce((sum, item) => {
    // Use finalPrice if available, otherwise fall back to price
    const price = item.finalPrice || item.price || 0;
    return sum + price * item.quantity;
  }, 0);
export const selectCartTotal = (state) => {
  const subtotal = selectCartSubtotal(state);
  const tax = subtotal * 0.08; // Fallback tax; authoritative value comes from backend when available
  
  // Apply coupon discount if available
  const couponDiscount = state.cart.coupon?.discountAmount || 0;
  
  // If we have backend totals, use them (backend already includes discount in total)
  if (state.cart.totals) {
    // Backend totals already include the discountAmount we passed
    return state.cart.totals.total;
  }
  
  // Fallback to local calculation with coupon discount
  // Note: Shipping is 0 in fallback - should be calculated via state selection
  const shipping = 0;
  return subtotal + shipping + tax - couponDiscount;
};
export const selectCartTotals = (state) => state.cart.totals;
export const selectCartTotalsLoading = (state) => state.cart.totalsLoading;
export const selectCartCoupon = (state) => state.cart.coupon;
export const selectCartCouponLoading = (state) => state.cart.couponLoading;
export const selectCartCouponError = (state) => state.cart.couponError;

export default cartSlice.reducer;

